import { useIntl } from 'react-intl-next';

import { type UserConvoAIStreamingConfig } from '@atlassian/ai-agent-streaming';
import type { AiFeatureRequestEnvelopeSuggestIssuesDocumentationRequest } from '@atlassian/convo-ai-api';

import { fg } from '@confluence/feature-gating';
import { mapActionItemsToIssues } from './actionItemUtils';
import { useIssueCreateSidePanelContext } from '../providers/IssueCreateSidePanelContextProvider';
import { useBulkCreateContext } from '../providers/BulkCreateContextProvider';

export const useBuildIssueSuggestionsRequest = (
	markdownPlusContent: string,
	pageAri: string,
	isConvoAIMigrationEnabled: boolean,
	overrideXProduct?: string,
): [AiFeatureRequestEnvelopeSuggestIssuesDocumentationRequest, UserConvoAIStreamingConfig] => {
	const { locale } = useIntl();
	const [{ isCreatedFromActionItems }] = useBulkCreateContext();
	const [{ selectedActionItems }] = useIssueCreateSidePanelContext();

	const isActionItemCreation =
		isCreatedFromActionItems &&
		selectedActionItems &&
		fg('confluence-create-work-items-from-action-items');
	const mappedIssues = isActionItemCreation ? mapActionItemsToIssues(selectedActionItems) : [];

	return [
		{
			ai_feature_input: {
				context: {
					primary_text: isActionItemCreation ? ' ' : markdownPlusContent,
					...(isActionItemCreation ? { highlighted_text: selectedActionItems.innerHTML } : {}),
					input_mime_type: 'text/markdown',
					source_ari: pageAri,
				},
				source: 'CONFLUENCE',
				locale,
				suggested_issues_config: {
					max_issues: isActionItemCreation ? mappedIssues.length : 10,
					suggested_issue_field_types: [
						{
							issue_type: 'Task',
							fields: [
								{
									field_name: 'Summary',
									field_type: 'Short-Text',
								},
								{
									field_name: 'Description',
									field_type: 'Paragraph',
								},
							],
						},
					],
				},
				sourceType: 'string',
			},
		},
		{
			apiPath: 'v2/ai-feature/jira/issue/source-type/documentation/suggestions/stream',
			enablePartialJson: false,
			skip: !markdownPlusContent || !isConvoAIMigrationEnabled,
			headers: {
				'x-experience-id': 'ai-issue-create-confluence-bulk',
				'x-product':
					overrideXProduct && fg('confluence-create-work-items-from-action-items')
						? overrideXProduct
						: 'confluence',
			},
		},
	];
};
